// src/components/resources/calculators/video-dropoff/components/PlatformSelector.tsx

/**
 * @file Platform Selector Component
 * @description Platform selection component for video dropoff calculator
 */

import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import RiFacebookCircleLine from "~/icons/remix/generated/RiFacebookCircleLine"
import RiTiktokLine from "~/icons/remix/generated/RiTiktokLine"
import RiYoutubeLine from "~/icons/remix/generated/RiYoutubeLine"
import RiVimeoLine from "~/icons/remix/generated/RiVimeoLine"
import RiLinksLine from "~/icons/remix/generated/RiLinksLine"
import RiInformationLine from "~/icons/remix/generated/RiInformationLine"
import type { Platform } from '~/components/resources/calculators/video-dropoff/types'

/**
 * Props for the PlatformSelector component
 */
interface PlatformSelectorProps {
  /** Currently selected platform */
  selectedPlatform: Platform
  /** Callback when platform is changed */
  onPlatformChange: (platform: Platform) => void
}

/**
 * Platform selection component with animated indicators
 * @param {PlatformSelectorProps} props - Component props
 * @returns {JSX.Element} Rendered platform selector
 */
export const PlatformSelector = ({
  selectedPlatform,
  onPlatformChange
}: PlatformSelectorProps): JSX.Element => {
  const platforms = [
    {
      id: 'facebook' as const,
      label: 'Facebook',
      icon: RiFacebookCircleLine,
      description: 'Facebook provides key metrics at 3s, 15s (ThruPlay), and percentage-based breakpoints.'
    },
    {
      id: 'tiktok' as const,
      label: 'TikTok', 
      icon: RiTiktokLine,
      description: 'TikTok emphasizes early retention (2s, 6s) and percentage-based breakpoints.'
    },
    {
      id: 'youtube' as const,
      label: 'YouTube',
      icon: RiYoutubeLine,
      description: 'YouTube analytics focus on percentage-based breakpoints (25%, 50%, 75%, 100%).'
    },
    {
      id: 'vimeo' as const,
      label: 'Vimeo',
      icon: RiVimeoLine,
      description: 'Vimeo reports percentage-based engagement (plays vs. finishes), so analysis uses 25%, 50%, 75%, and 100% breakpoints.'
    },
    {
      id: 'custom' as const,
      label: 'Custom',
      icon: RiLinksLine,
      description: 'Create custom breakpoints to analyze specific moments in your video.'
    }
  ]

  return (
    <div className="mt-8 px-8" role="radiogroup" aria-labelledby="platform-label">
      <label id="platform-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Video Platform
      </label>
      <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {platforms.map(({ id, label, icon: Icon }) => (
          <motion.button
            key={id}
            onClick={() => onPlatformChange(id)}
            aria-pressed={selectedPlatform === id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "relative flex flex-col items-center justify-center gap-2 rounded-lg border p-4 text-sm font-medium shadow-sm",
              selectedPlatform === id
                ? "border-indigo-500 bg-indigo-50 text-indigo-600 dark:border-indigo-400 dark:bg-indigo-900/20 dark:text-indigo-400"
                : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            )}
          >
            <Icon className="h-8 w-8" aria-hidden="true" />
            <span>{label}</span>
            {selectedPlatform === id && (
              <motion.div 
                layoutId="platformIndicator"
                className="absolute -right-1 -top-1 h-6 w-6 rounded-full bg-indigo-500 flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="white" strokeWidth="3" fill="none">
                  <polyline points="6 12 10 16 18 8"></polyline>
                </svg>
              </motion.div>
            )}
          </motion.button>
        ))}
      </div>
      
      {selectedPlatform && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-3 text-sm text-gray-500 dark:text-gray-400 flex items-center"
        >
          <RiInformationLine className="h-4 w-4 mr-1.5 text-indigo-500" />
          {platforms.find(p => p.id === selectedPlatform)?.description}
        </motion.div>
      )}
    </div>
  )
}
